import { AuthRoute, NotFound, Redirect, Route, Switch, useLocation } from '@fs/zion-router'
import { Row, Separator, Tab, Tabs, H1, Paragraph } from '@fs/zion-ui'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { getPathElement } from '../util/StringUtil'
import { PermissionsContext } from './app/PermissionsContext'
import SubHeader from './app/SubHeader'
import ArtifactsBase from './artifacts/ArtifactsBase'
import GroupBase from './group/GroupBase'
import PersonBase from './person/PersonBase'
import QueuesBase from './queues/QueuesBase'
import RelationshipBase from './relationship/RelationshipBase'
import SearchBase from './search/SearchBase'
import TempleBase from './temple/TempleBase'
import ToolsBase from './tools/ToolsBase'
import { UnitBase } from './unit/UnitBase'
import UserBase from './user/UserBase'
import ChatBase from './chat/ChatBase'
import TreeBase from './tree/TreeBase'

export default function MainContent() {
  const { isPreferredRole, isAuthorized, isRole, permissionsLoading } = useContext(PermissionsContext)
  const location = useLocation()
  const { t } = useTranslation()
  const canViewTemple = () => {
    return (
      isAuthorized('tss_ReadTempleProfilePermission') ||
      isAuthorized('tss_UpdateTempleProfilePermission') ||
      isAuthorized('tss_CreateTempleProfilePermission') ||
      isAuthorized('tss_ReadInventoryItemPermission') ||
      isAuthorized('tss_ReadCtrControlledPermission') ||
      isAuthorized('tss_ReadCtrFullAcdPermission') ||
      isAuthorized('tss_ReadOwsPermission') ||
      isAuthorized('tss_ReadLorPermission') ||
      isAuthorized('tss_ReadForPermission') ||
      isAuthorized('tss_ReadTempleListPermission') ||
      isAuthorized('tss_ReadKioskPermission') ||
      isAuthorized('tss_ModifyKioskPermission') ||
      isAuthorized('tss_DeleteKioskPermission') ||
      isAuthorized('tss_CreateKioskPermission')
    )
  }

  const tabs = []
  tabs.push(<Tab key="person" to="/person" title={t('person.label', 'Person')} />)
  tabs.push(<Tab key="relationship" to="/relationship" title={t('relationship.label', 'Relationship')} />)
  if (canViewTemple()) {
    tabs.push(<Tab key="temple" to="/temple" title={t('temple.label', 'Temple')} />)
  }
  tabs.push(<Tab key="user" to="/user" title={t('user.label', 'User')} />)
  if (isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    tabs.push(<Tab key="chat" to="/chat" title={t('chat', 'Chat')} />)
  }
  if (isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    tabs.push(<Tab key="group" to="/group" title={t('group.label', 'Group')} />)
  }
  if (isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    tabs.push(<Tab key="tree" to="/tree" title={t('tree.label', 'Tree')} />)
  }
  tabs.push(<Tab key="unit" to="/unit" title={t('nav.unit', 'Unit')} />)
  if (isAuthorized('ReadFailedMessagePermission')) {
    tabs.push(<Tab key="queues" to="/queues" title={t('nav.queues', 'Queues')} />)
  }
  if (isAuthorized('ArtPrmCanReview')) {
    tabs.push(<Tab key="artifacts" to="/artifacts" title={t('artifacts.label', 'Artifacts')} />)
  }
  if (
    isAuthorized('ViewAllCmisxData') ||
    isAuthorized(['tss_ReadCtrControlledPermission', 'tss_ReadCtrFullAcdPermission'])
  ) {
    tabs.push(<Tab key="search" to="/search" title={t('action.search', 'Search')} />)
  }
  tabs.push(<Tab key="tools" to="/tools" title={t('nav.tools', 'Tools')} />)

  if (!isPreferredRole(['', null]) && !isRole('')) {
    return (
      <>
        <SubHeader />
        <div>
          <Tabs index={tabs.findIndex((tab) => tab.key === getPathElement(location.pathname, 0))}>{tabs}</Tabs>
          <Separator size="xxs" />
        </div>
        <Switch>
          <Redirect from="/" exact to="/person" />
          <AuthRoute path="/person/:personId" component={PersonBase} />
          <AuthRoute path="/person" component={PersonBase} />
          <AuthRoute path="/relationship/:relationshipType/:relationshipId" component={RelationshipBase} />
          <AuthRoute path="/relationship/:relationshipId" component={RelationshipBase} />
          <AuthRoute path="/relationship" component={RelationshipBase} />
          {canViewTemple() && <AuthRoute path="/temple" component={TempleBase} />}
          <AuthRoute path="/user/:userId" component={UserBase} />
          <AuthRoute path="/user" component={UserBase} />
          <AuthRoute path="/unit/:unitId" component={UnitBase} />
          <AuthRoute path="/unit" component={UnitBase} />
          <AuthRoute path="/queues" component={QueuesBase} />
          <AuthRoute path="/artifacts" component={ArtifactsBase} />
          <AuthRoute path="/search" component={SearchBase} />
          <AuthRoute path="/tools" component={ToolsBase} />
          {isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <AuthRoute path="/group/:groupId" component={GroupBase} />
          )}
          {isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <AuthRoute path="/group" component={GroupBase} />
          )}
          {isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <AuthRoute path="/tree/:treeId" component={TreeBase} />
          )}
          {isRole(['Engineer Admin', 'Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <AuthRoute path="/tree" component={TreeBase} />
          )}
          {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && (
            <AuthRoute path="/chat/:channelId" component={ChatBase} />
          )}
          {isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6']) && <AuthRoute path="/chat" component={ChatBase} />}
          {!permissionsLoading && <Route component={NotFound} />}
        </Switch>
      </>
    )
  }
  return (
    <>
      <Separator size="xl" />
      <Row alignX="center">
        <H1 size="H5">{t('welcome', 'Welcome to the Data Management Console')}</H1>
      </Row>
      <Paragraph centered>
        {t(
          'dmcRequiresElevationMessage',
          'The use of the Data Management Console requires elevating yourself to an Admin role.  Currently you are not elevated, so please elevate now.'
        )}
      </Paragraph>
    </>
  )
}
